/**
 * Seletor de ciclo GLOBAL — o "switch" do ciclo ativo. Sempre visível nos dois
 * shells (topo da sidebar no desktop; barra fina persistente no mobile). Trocar
 * aqui muda `avaliacaoId` no estado; como todas as páginas derivam dele, elas
 * re-renderizam para o novo ciclo sem recarregar o app (RF2).
 *
 * A lista é a mesma nos dois (função pura `ciclosDisponiveis`). Rótulo em duas
 * linhas via `rotularCiclo` (L1 unidade; L2 fase · MM/AAAA + status).
 */
import { Icon, Sheet } from "@caint/design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AvaliacaoRemota } from "../lib/api.ts";
import { type CicloDisponivel, ciclosDisponiveis, rotularCiclo } from "../lib/ciclos.ts";

export interface SeletorCicloProps {
  avaliacoes: readonly AvaliacaoRemota[];
  avaliacaoId: string | null;
  onSelecionar: (avaliacaoId: string) => void;
  /** Abre a tela de gestão de Ciclos (listagem/filtros/abrir). */
  onGerir?: () => void;
}

function StatusBadge({ aberto }: { aberto: boolean }) {
  return <span className={`ciclo-badge ${aberto ? "on" : "off"}`}>{aberto ? "Aberto" : "Encerrado"}</span>;
}

/** Lista de ciclos compartilhada (desktop popover e mobile sheet). */
function ListaCiclos({
  ciclos,
  ativoId,
  onEscolher,
}: {
  ciclos: readonly CicloDisponivel[];
  ativoId: string | null;
  onEscolher: (id: string) => void;
}) {
  if (ciclos.length === 0) {
    return <div className="ciclo-vazio">Nenhum ciclo disponível.</div>;
  }
  return (
    <div className="ciclo-lista" aria-label="Ciclos disponíveis">
      {ciclos.map((c) => (
        <button
          key={c.avaliacaoId}
          type="button"
          aria-pressed={c.avaliacaoId === ativoId}
          className={`ciclo-opt${c.avaliacaoId === ativoId ? " ativo" : ""}`}
          onClick={() => onEscolher(c.avaliacaoId)}
        >
          <span className="ciclo-opt-un">{c.unidade}</span>
          <span className="ciclo-opt-sub">
            {c.fase} · {c.periodo}
            <StatusBadge aberto={c.aberto} />
          </span>
        </button>
      ))}
    </div>
  );
}

/* ---------------- Guardrail: tela bloqueada sem ciclo selecionado ---------------- */

/**
 * Estado bloqueante das telas com escopo de ciclo (ver `exigeCicloSelecionado`).
 *
 * Existe porque TODA escrita do estado é silenciosamente descartada sem
 * `avaliacaoAtual`: a supervisora preenchia a Supervisão AUDIN inteira numa tela
 * que parecia editável e nada era gravado nem enviado (incidente de 17/ago/2026,
 * Barreirinhas). Em vez de só avisar, a tela não abre — e traz a lista aqui
 * dentro para resolver no mesmo lugar, sem mandar o usuário procurar o seletor.
 */
export function SemCicloSelecionado({ avaliacoes, avaliacaoId, onSelecionar, onGerir }: SeletorCicloProps) {
  const ciclos = useMemo(() => ciclosDisponiveis(avaliacoes), [avaliacoes]);
  const nAbertos = ciclos.filter((c) => c.aberto).length;
  return (
    <div className="ciclo-gate">
      <div className="ciclo-gate-ic" aria-hidden="true">
        <Icon name="layers" size={22} />
      </div>
      <h2 className="ciclo-gate-tit">Selecione um ciclo para continuar</h2>
      <p className="ciclo-gate-sub">
        Esta tela pertence a um ciclo de avaliação. Escolha a unidade abaixo para que o preenchimento seja
        salvo no lugar certo.
      </p>
      <div className="ciclo-gate-lista">
        <ListaCiclos ciclos={ciclos} ativoId={avaliacaoId} onEscolher={onSelecionar} />
      </div>
      {ciclos.length > 0 && (
        <p className="ciclo-gate-pe">
          {nAbertos} ciclo(s) dentro da janela · {ciclos.length} no total
        </p>
      )}
      {onGerir && (
        <button type="button" className="m-btn" onClick={onGerir}>
          Gerenciar ciclos
        </button>
      )}
    </div>
  );
}

/* ---------------- Desktop — bloco no topo da sidebar ---------------- */

export function SeletorCicloDesktop({ avaliacoes, avaliacaoId, onSelecionar, onGerir }: SeletorCicloProps) {
  const [aberto, setAberto] = useState(false);
  const ciclos = useMemo(() => ciclosDisponiveis(avaliacoes), [avaliacoes]);
  const atual = useMemo(() => rotularCiclo(avaliacoes, avaliacaoId), [avaliacoes, avaliacaoId]);
  const nAbertos = ciclos.filter((c) => c.aberto).length;

  return (
    <div className="dk-ciclo">
      <button
        type="button"
        className="dk-ciclo-btn"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
      >
        <span className="dk-ciclo-ic">
          <Icon name="layers" size={16} />
        </span>
        <span className="dk-ciclo-cur">
          {atual ? (
            <>
              <span className="dk-ciclo-un">{atual.unidade}</span>
              <span className="dk-ciclo-sub">
                {atual.fase} · {atual.periodo}
                <StatusBadge aberto={atual.aberto} />
              </span>
            </>
          ) : (
            <>
              <span className="dk-ciclo-un">Selecione um ciclo</span>
              <span className="dk-ciclo-sub">{nAbertos} aberto(s) · escolha</span>
            </>
          )}
        </span>
        <span className={`dk-ciclo-chev${aberto ? " on" : ""}`}>
          <Icon name="chevron" size={15} />
        </span>
      </button>
      {aberto && (
        <>
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: overlay só fecha ao clicar fora */}
          <div className="dk-ciclo-scrim" onClick={() => setAberto(false)} />
          <div className="dk-ciclo-pop">
            <ListaCiclos
              ciclos={ciclos}
              ativoId={avaliacaoId}
              onEscolher={(id) => {
                onSelecionar(id);
                setAberto(false);
              }}
            />
            {onGerir && (
              <button
                type="button"
                className="dk-ciclo-gerir"
                onClick={() => {
                  setAberto(false);
                  onGerir();
                }}
              >
                Gerenciar ciclos…
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Mobile — chips segmentados deslizáveis + bottom-sheet ---------------- */

export function BarraCicloMobile({ avaliacoes, avaliacaoId, onSelecionar, onGerir }: SeletorCicloProps) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const ciclos = useMemo(() => ciclosDisponiveis(avaliacoes), [avaliacoes]);
  const atual = useMemo(() => rotularCiclo(avaliacoes, avaliacaoId), [avaliacoes, avaliacaoId]);
  const nAbertos = ciclos.filter((c) => c.aberto).length;

  // Chips de troca rápida = ciclos abertos + o ativo (mesmo encerrado, p/ ficar visível).
  // O resto (encerrados/todas as unidades) fica a um toque no botão de busca.
  const chips = useMemo(() => {
    const abertos = ciclos.filter((c) => c.aberto);
    if (avaliacaoId && !abertos.some((c) => c.avaliacaoId === avaliacaoId)) {
      const ativo = ciclos.find((c) => c.avaliacaoId === avaliacaoId);
      if (ativo) return [ativo, ...abertos];
    }
    return abertos;
  }, [ciclos, avaliacaoId]);
  const restantes = ciclos.length - chips.length;

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return q ? ciclos.filter((c) => c.unidade.toLowerCase().includes(q)) : ciclos;
  }, [ciclos, busca]);

  const fechar = () => {
    setAberto(false);
    setBusca("");
  };

  // Centraliza o chip ativo quando a seleção muda (sem mover a página, só o scroller).
  const scrollRef = useRef<HTMLDivElement>(null);
  const ativoRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!avaliacaoId || chips.length === 0) return;
    const cont = scrollRef.current;
    const chip = ativoRef.current;
    if (!cont || !chip) return;
    const alvo = chip.offsetLeft - cont.clientWidth / 2 + chip.clientWidth / 2;
    cont.scrollTo({ left: Math.max(0, alvo), behavior: "smooth" });
  }, [avaliacaoId, chips]);

  return (
    <>
      <div className={`m-uniswitch${atual ? "" : " vazio"}`}>
        <span className="m-uniswitch-lead">
          <Icon name="layers" size={16} />
          <span>{atual ? "Unidade" : "Escolha"}</span>
        </span>
        {/* Cada chip carrega aria-label completo; o grupo em si dispensa role. */}
        <div className="m-uniswitch-scroll" ref={scrollRef} aria-label="Trocar unidade (ciclo ativo)">
          {chips.length === 0 ? (
            <span className="m-uniswitch-vazio">Nenhum ciclo aberto</span>
          ) : (
            chips.map((c) => {
              const ativoChip = c.avaliacaoId === avaliacaoId;
              return (
                <button
                  key={c.avaliacaoId}
                  ref={ativoChip ? ativoRef : undefined}
                  type="button"
                  aria-pressed={ativoChip}
                  aria-label={`${c.unidade}, ${c.fase} ${c.periodo}${ativoChip ? ", unidade ativa" : ""}`}
                  title={`${c.unidade} · ${c.fase} · ${c.periodo}`}
                  className={`m-unichip${ativoChip ? " on" : ""}`}
                  onClick={() => onSelecionar(c.avaliacaoId)}
                >
                  {ativoChip && <span className="m-unichip-dot" aria-hidden="true" />}
                  <span className="m-unichip-nome">{c.unidade}</span>
                </button>
              );
            })
          )}
        </div>
        <button
          type="button"
          className="m-uniswitch-more"
          aria-label="Buscar em todas as unidades e ciclos"
          onClick={() => setAberto(true)}
        >
          <Icon name="search" size={16} />
          {restantes > 0 && <span>+{restantes}</span>}
        </button>
      </div>
      {aberto && (
        <Sheet
          title="Trocar ciclo"
          sub={`${nAbertos} aberto(s) · ${ciclos.length} no total`}
          onClose={fechar}
          foot={
            onGerir ? (
              <button
                type="button"
                className="m-btn"
                onClick={() => {
                  fechar();
                  onGerir();
                }}
              >
                Gerenciar ciclos
              </button>
            ) : undefined
          }
        >
          <input
            className="m-input"
            style={{ marginBottom: 12 }}
            placeholder="Buscar unidade…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <ListaCiclos
            ciclos={filtrados}
            ativoId={avaliacaoId}
            onEscolher={(id) => {
              onSelecionar(id);
              fechar();
            }}
          />
        </Sheet>
      )}
    </>
  );
}
